/**
 * Component Registry for Data Views V2
 *
 * Centralized registry mapping Type-ReferenceUnit combinations to their UI components.
 *
 * Key format: "TypeName-ReferenceUnitName"
 *
 * Architecture:
 * - Built-in types use factory pattern: export a function that receives dependencies
 * - INJECTABLE_DEPS provides all Vue/store dependencies to factory functions
 * - COMPONENT_REGISTRY is populated by calling factory functions with deps
 * - Lookup functions check built-in registry; getComponentsAsync also tries
 *   configurable types loaded dynamically from the filesystem
 *
 * Note: the factory pattern is slated for deprecation. Until then, type-view
 * .js files are kept as-is (plain ES module factories).
 */

import * as Vue from 'vue';
import { useReferenceUnitStore } from '../../stores/referenceUnitStore';
import { useMatterStore } from '../../stores/matterStore';
import { useDataViewStore } from '../../stores/dataViewStore';
import { formatDate } from '../../utils/dateFormatting';
import { useRowSelection } from '../../composables/useRowSelection';
import RawViewContainer from './RawViewContainer.vue';
import CommunicationEventItem from './CommunicationEventItem.vue';
import EdocTextDisplay from './EdocTextDisplay.vue';
import ReferenceRowTags from './ReferenceRowTags.vue';

// Factory imports — the Vite/Rollup loader can't statically discover dynamic
// imports, so each built-in type's factories are imported by name here.
import createEdocReferencesView from './types/EDoc/ReferencesView.js';
import createEdocRawView from './types/EDoc/RawView.js';
import createConversationReferencesView from './types/EmailCommunication/ConversationReferencesView.js';
import createCommunicationRawView from './types/EmailCommunication/CommunicationRawView.js';
import createConversationDayReferencesView from './types/EmailCommunication/ConversationDayReferencesView.js';
import createConversationDayRawView from './types/EmailCommunication/ConversationDayRawView.js';
import createChatConversationDayReferencesView from './types/ChatMessaging/ConversationDayReferencesView.js';
import createChatConversationDayRawView from './types/ChatMessaging/ConversationDayRawView.js';

// Shared CDM views — one references view and one raw view used by every CDM
// type across all four domains (CRM / HRIS / Finance / Procurement). Both are
// driven entirely by the per-type display_spec from ToolsV2Metadata, so there
// are no domain-specific view files.
import createCdmReferencesView from './types/Cdm/ReferencesView.js';
import createCdmRawView from './types/Cdm/RawView.js';

// `unknown` for the factory and component shape — these are plain ES module
// factories whose returned component objects are used as `:is` targets.
// Strict typing here would force a churn-y interface that goes away when the
// factory pattern is deprecated.
type Factory = (deps: typeof INJECTABLE_DEPS) => unknown;

interface RegistryEntry {
    referenceView: unknown;
    rawView: unknown;
}

// Dependencies injected into factory components.
// All factory functions receive this object and destructure what they need.
//
// `useProjectStore` is a deprecated alias for `useMatterStore` kept for
// runtime-unpacked configurable types that were packaged before the
// frontend rename. Drop it once the factory pattern is dismantled.
const INJECTABLE_DEPS = {
    ref: Vue.ref,
    computed: Vue.computed,
    watch: Vue.watch,
    onMounted: Vue.onMounted,
    inject: Vue.inject,
    useReferenceUnitStore,
    useMatterStore,
    useProjectStore: useMatterStore,
    useDataViewStore,
    RawViewContainer,
    CommunicationEventItem,
    EdocTextDisplay,
    ReferenceRowTags,
    formatDate,
    useRowSelection,
};

const f = (factory: Factory): unknown => factory(INJECTABLE_DEPS);

// CDM type groupings by domain
const HRIS_TYPES = [
    'HrisWorker', 'HrisPayrollRun', 'HrisLeaveRequest', 'HrisPerformanceReview',
    'HrisBenefitsEnrollment', 'HrisTrainingRecord', 'HrisEmployeeHiredEvent',
    'HrisEmployeeTerminatedEvent', 'HrisPayrollRunCompletedEvent', 'HrisLeaveRequestApprovedEvent',
    'HrisPerformanceReviewCompletedEvent', 'HrisPromotionRoleChangeEvent',
    'HrisBenefitsEnrollmentEvent', 'HrisTrainingCompletedEvent',
];

const CRM_TYPES = [
    'CrmUser', 'CrmAccount', 'CrmContact', 'CrmLead', 'CrmOpportunity', 'CrmQuote',
    'CrmSupportTicket', 'CrmSurveyResponse', 'CrmContactCreatedEvent', 'CrmLeadCreatedEvent',
    'CrmOpportunityCreatedEvent', 'CrmOpportunityOpenedEvent', 'CrmQuoteSentEvent',
    'CrmDealWonLostEvent', 'CrmSupportTicketOpenedEvent', 'CrmSupportTicketResolvedEvent',
    'CrmCustomerSurveyCompletedEvent',
];

const FINANCE_TYPES = [
    'FinanceUser', 'FinanceJournalEntry', 'FinanceAccountingPeriod', 'FinanceBudget',
    'FinanceBankReconciliation', 'FinanceFixedAsset', 'FinanceMonthEndCloseEvent',
    'FinanceBudgetExceededEvent', 'FinanceJournalEntryCreatedEvent',
    'FinanceBankReconciliationCompletedEvent', 'FinanceAssetDepreciationRunEvent',
];

// Formerly PROC_TYPES. The Procurement domain was renamed to ERP, and the
// Finance Invoice/Payment types (+ their events) moved into it — so these keys
// must match the backend Erp* type_names or the shared CDM view won't resolve.
const ERP_TYPES = [
    'ErpUser', 'ErpSupplier', 'ErpPurchaseRequisition', 'ErpPurchaseOrder',
    'ErpSupplierQuote', 'ErpGoodsReceipt', 'ErpInvoice', 'ErpPayment', 'ErpContract',
    'ErpPurchaseReqSubmittedEvent', 'ErpPurchaseOrderCreatedEvent',
    'ErpSupplierQuoteReceivedEvent', 'ErpPOApprovedEvent', 'ErpGoodsReceiptPostedEvent',
    'ErpInvoicePostedEvent', 'ErpInvoiceMatchedEvent', 'ErpPaymentProcessedEvent',
    'ErpContractRenewedEvent', 'ErpSupplierOnboardedEvent',
];

export const COMPONENT_REGISTRY: Record<string, RegistryEntry> = {
    'EDoc-EDoc': {
        referenceView: f(createEdocReferencesView),
        rawView: f(createEdocRawView),
    },
    'EmailCommunication-Conversation': {
        referenceView: f(createConversationReferencesView),
        rawView: f(createCommunicationRawView),
    },
    'EmailCommunication-ConversationDay': {
        referenceView: f(createConversationDayReferencesView),
        rawView: f(createConversationDayRawView),
    },
    'ChatMessaging-ConversationDay': {
        referenceView: f(createChatConversationDayReferencesView),
        rawView: f(createChatConversationDayRawView),
    },
};

// All CDM types share one references view and one raw view (both driven by
// per-type display_spec from ToolsV2Metadata). Build each once and reuse the
// instances across every type registration below — the factories have no
// per-type state.
const cdmReferencesView = f(createCdmReferencesView);
const cdmRawView = f(createCdmRawView);

// Register every CDM type across the four domains with the shared views.
for (const typeName of [...HRIS_TYPES, ...CRM_TYPES, ...FINANCE_TYPES, ...ERP_TYPES]) {
    COMPONENT_REGISTRY[`${typeName}-${typeName}`] = {
        referenceView: cdmReferencesView,
        rawView: cdmRawView,
    };
}

export function getReferenceViewComponent(
    typeName: string,
    referenceUnitName: string,
): unknown | null {
    const key = `${typeName}-${referenceUnitName}`;
    const entry = COMPONENT_REGISTRY[key];
    return entry?.referenceView || null;
}

export function getRawViewComponent(typeName: string, referenceUnitName: string): unknown | null {
    const key = `${typeName}-${referenceUnitName}`;
    const entry = COMPONENT_REGISTRY[key];
    return entry?.rawView || null;
}

export function hasCompleteRegistration(typeName: string, referenceUnitName: string): boolean {
    const key = `${typeName}-${referenceUnitName}`;
    const entry = COMPONENT_REGISTRY[key];
    return !!(entry?.referenceView && entry?.rawView);
}

export function validateRegistry(): string[] {
    const incomplete: string[] = [];
    for (const [key, entry] of Object.entries(COMPONENT_REGISTRY)) {
        if (!entry.referenceView || !entry.rawView) {
            incomplete.push(key);
        }
    }
    return incomplete;
}

/**
 * Load components for a configurable type from disk.
 * Components must use the factory pattern.
 *
 * No caching — each call attempts a fresh import().
 * Browser handles HTTP-level caching (304 Not Modified).
 * If type is disabled (files deleted), import fails with 404.
 */
async function loadConfigurableTypeComponents(
    matterId: string,
    typeName: string,
): Promise<RegistryEntry> {
    const basePath = `/runtime_types/${matterId}/${typeName}`;

    const [referencesModule, rawModule] = await Promise.all([
        import(/* @vite-ignore */ `${basePath}/ReferencesView.js`),
        import(/* @vite-ignore */ `${basePath}/RawView.js`),
    ]);

    return {
        referenceView: (referencesModule.default as Factory)(INJECTABLE_DEPS),
        rawView: (rawModule.default as Factory)(INJECTABLE_DEPS),
    };
}

/**
 * Async component lookup — checks built-in first, then configurable.
 * For configurable types, assumes ReferenceUnit matches TypeName.
 */
export async function getComponentsAsync(
    typeName: string,
    referenceUnitName: string,
    matterId: string | null,
): Promise<RegistryEntry | null> {
    const key = `${typeName}-${referenceUnitName}`;

    if (COMPONENT_REGISTRY[key]) {
        return COMPONENT_REGISTRY[key];
    }

    if (matterId) {
        try {
            return await loadConfigurableTypeComponents(matterId, typeName);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            console.warn(`[ComponentRegistry] Could not load ${typeName}:`, msg);
            return null;
        }
    }

    return null;
}
